'use client';

import { c as _c } from "react/compiler-runtime";
import type { Profile } from '@channel.io/channel-web-sdk-loader';
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { captureException } from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ENV, WELLO_IMAGE_PREFIX_URL } from '@/constants';
import { ROUTES, WEL_MARKET_ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/AuthStore';
import imageLoader from '@/utils/imageLoader';
import { toChannelBootError } from '@/utils/toChannelBootError';
export const ChannelTalk = () => {
  const $ = _c(18);
  const pathname = usePathname();
  const [isLogin, myInfo] = useAuthStore(useShallow(_temp));
  const [unreadCount, setUnreadCount] = useState(0);
  const [alertCount, setAlertCount] = useState(0);
  const [isMessengerVisible, setIsMessengerVisible] = useState(false);
  const isMessengerVisibleRef = useRef(false);
  const isChannelLoadedRef = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [ROUTES.HOME.pathname, ROUTES.BENEFIT.pathname];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const hiddenExactPathnames = t0;
  let t1;
  if ($[1] !== pathname) {
    const hiddenPathPatterns = [ROUTES.LOGIN.pathname, ROUTES.AUTH.pathname, ROUTES.EASY_CERTIFICATION.pathname, ROUTES.MY_EDIT.pathname, ROUTES.SEARCH.pathname, ROUTES.COMMUNITY.pathname, ROUTES.RECOMMEND_POLICY_LIST.pathname, ROUTES.MEDIAN_INCOME.pathname, "/join", "/kb-join", "/event", "/donation/event", `${WEL_MARKET_ROUTES.HOME.pathname}/order`, ROUTES.WELLO_AGENT.pathname, `${ROUTES.BENEFIT.pathname}/`];
    const hiddenDetailRoutes = [ROUTES.POLICY_DETAIL, ROUTES.ARTICLE_DETAIL, ROUTES.FESTIVAL_DETAIL];
    t1 = hiddenExactPathnames.includes(pathname ?? "") || hiddenPathPatterns.some(pattern => pathname?.startsWith(pattern)) || hiddenDetailRoutes.some(route => route.isMatchPathname(pathname ?? ""));
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isHiddenByPath = t1;
  const shouldHide = isMessengerVisible || isHiddenByPath;
  const handleChannelButtonClick = _temp2;
  let t2;
  let t3;
  if ($[3] !== isHiddenByPath || $[4] !== isLogin || $[5] !== myInfo) {
    t2 = () => {
      const pluginKey = ENV.NEXT_PUBLIC_CHANNEL_TALK_PLUGIN_KEY;
      if (!pluginKey) {
        return;
      }
      if (isHiddenByPath) {
        return;
      }
      const toProfile = _temp6;
      const initChannelTalk = () => {
        ChannelService.loadScript();
        isChannelLoadedRef.current = true;
        ChannelService.boot({
          pluginKey,
          customLauncherSelector: "#custom-channel-button",
          hideChannelButtonOnBoot: true,
          ...(isLogin && myInfo ? {
            ...(myInfo.memberOAuth !== undefined ? {
              memberId: myInfo.memberOAuth.toString()
            } : {}),
            profile: toProfile(myInfo as unknown as Record<string, unknown>)
          } : {})
        }, _temp7);
        ChannelService.onBadgeChanged((unread, alert) => {
          setUnreadCount(unread);
          setAlertCount(alert);
        });
        ChannelService.onShowMessenger(() => {
          setIsMessengerVisible(true);
          isMessengerVisibleRef.current = true;
        });
        ChannelService.onHideMessenger(() => {
          setIsMessengerVisible(false);
          isMessengerVisibleRef.current = false;
        });
        ChannelService.onUrlClicked(() => {
          if (!isMessengerVisibleRef.current) {
            ChannelService.track("PopupBannerClick");
          }
        });
      };
      const idleCallbackId = typeof requestIdleCallback !== "undefined" ? requestIdleCallback(initChannelTalk, {
        timeout: 5000
      }) : undefined;
      const timeoutId = idleCallbackId === undefined ? setTimeout(initChannelTalk, 3000) : undefined;
      return () => {
        if (idleCallbackId !== undefined) {
          cancelIdleCallback(idleCallbackId);
        }
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        if (isChannelLoadedRef.current) {
          ChannelService.clearCallbacks();
          ChannelService.shutdown();
          isChannelLoadedRef.current = false;
        }
      };
    };
    t3 = [isLogin, myInfo, isHiddenByPath];
    $[3] = isHiddenByPath;
    $[4] = isLogin;
    $[5] = myInfo;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  if (shouldHide) {
    return null;
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = imageLoader({
      src: `${WELLO_IMAGE_PREFIX_URL}/static/ch-talk.png`,
      width: 120,
      quality: 75
    });
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const chTalkUrl = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      position: "fixed",
      right: "20px",
      bottom: "calc(20px + max(var(--bottom-inset, 0px), 60px))",
      zIndex: 10
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `url(${chTalkUrl})`;
  let t7;
  if ($[10] !== t6) {
    t7 = <button type="button" id="custom-channel-button" onClick={handleChannelButtonClick} style={{
      position: "relative",
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      border: "none",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      padding: 0,
      backgroundImage: t6,
      backgroundSize: "cover",
      backgroundPosition: "center",
      transition: "transform 0.1s ease, box-shadow 0.1s ease"
    }} onMouseDown={_temp8} onMouseUp={_temp9} onMouseLeave={_temp10} aria-label={"\uCC44\uB110\uD1A1 \uC5F4\uAE30"} />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== alertCount || $[13] !== unreadCount) {
    t8 = alertCount > 0 ? <div style={{
      position: "absolute",
      top: "-4px",
      right: "-4px",
      minWidth: "20px",
      height: "20px",
      borderRadius: "10px",
      backgroundColor: "#ff4757",
      color: "white",
      fontSize: "12px",
      fontWeight: "bold",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0 6px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)"
    }}>{alertCount > 99 ? "99+" : alertCount}</div> : unreadCount > 0 ? <div style={{
      position: "absolute",
      top: "4px",
      right: "4px",
      width: "12px",
      height: "12px",
      borderRadius: "50%",
      backgroundColor: "#ff4757",
      border: "2px solid white",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)"
    }} /> : null;
    $[12] = alertCount;
    $[13] = unreadCount;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div style={t5}>{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
function _temp(state) {
  return [state.isLogin, state.myInfo] as const;
}
function _temp2() {
  ChannelService.showMessenger();
}
function _temp3(v_0) {
  return typeof v_0 === "string";
}
function _temp4(v) {
  return typeof v === "number";
}
function _temp5(acc, t0) {
  const [key, value] = t0;
  if (value === undefined) {
    return acc;
  }
  if (value === null) {
    acc[key] = null;
    return acc;
  }
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") {
    acc[key] = value as string | number | boolean;
    return acc;
  }
  if (Array.isArray(value)) {
    if (value.every(_temp3)) {
      acc[key] = value as string[];
    } else {
      if (value.every(_temp4)) {
        acc[key] = value as number[];
      }
    }
  }
  return acc;
}
function _temp6(obj) {
  return Object.entries(obj).reduce(_temp5, {} as Profile);
}
function _temp7(error) {
  if (error) {
    captureException(toChannelBootError(error), {
      extra: {
        bootError: error
      }
    });
  }
}
function _temp8(e) {
  e.currentTarget.style.transform = "scale(0.9)";
  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
}
function _temp9(e_0) {
  e_0.currentTarget.style.transform = "scale(1)";
  e_0.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
}
function _temp10(e_1) {
  e_1.currentTarget.style.transform = "scale(1)";
  e_1.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
}